import React, { useState, useEffect } from 'react';
import {
  Button,
  Text,
  Spinner,
  MessageBar,
} from '@fluentui/react-components';
import {
  CloudArrowUp24Regular,
  CheckmarkCircle24Regular,
  ErrorCircle24Regular,
  SignOut24Regular,
} from '@fluentui/react-icons';

import { Providers, ProviderState } from '@microsoft/mgt-element';
import '@microsoft/mgt-components';
import { useAppStore } from '@/stores/chatbotStore';
import SharePointBrowserModal from '@/components/SharePointBrowserModal';
import { SharePointFileInfo } from '@/types/api';

interface SharePointPickerProps {
  chatbotId: string;
  onUploadComplete: () => void;
}

const SharePointPicker: React.FC<SharePointPickerProps> = ({ chatbotId, onUploadComplete }) => {
  const { uploadSharePointFiles, isUploading } = useAppStore();

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<SharePointFileInfo[]>([]);
  const [isBrowserModalOpen, setIsBrowserModalOpen] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuthState = () => {
      try {
        if (Providers.globalProvider) {
          const state = Providers.globalProvider.state;
          console.log('Current provider state:', state);
          setIsSignedIn(state === ProviderState.SignedIn);
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        setError('Erreur lors de la vérification de l\'authentification');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthState();

    const handleStateChange = (e: any) => {
      console.log('Provider state changed:', e.detail);
      setIsSignedIn(e.detail === ProviderState.SignedIn);
      setError(null);
    };

    if (Providers.globalProvider) {
      Providers.globalProvider.onStateChanged(handleStateChange);
    }

    const interval = setInterval(checkAuthState, 2000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleSignIn = async () => {
    try {
      setError(null);
      if (Providers.globalProvider?.login) {
        await Providers.globalProvider.login();
      }
    } catch (error) {
      console.error('Sign in failed:', error);
      setError('Échec de la connexion SharePoint');
    }
  };

  const handleSignOut = async () => {
    try {
      setError(null);
      if (Providers.globalProvider?.logout) {
        await Providers.globalProvider.logout();
      }
      setSelectedFiles([]);
    } catch (error) {
      console.error('Sign out failed:', error);
      setError('Échec de la déconnexion');
    }
  };

  const handleFilesSelected = async (files: SharePointFileInfo[]) => {
    console.log('Files selected:', files);
    setSelectedFiles(files);
    setIsBrowserModalOpen(false);
  };

  const handleBrowseSharePoint = async () => {
    try {
      setError(null);
      setIsBrowserModalOpen(true);
    } catch (error) {
      console.error('Erreur lors de l\'ouverture du navigateur SharePoint:', error);
      setError('Impossible d\'ouvrir le navigateur de fichiers SharePoint');
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setError(null);

      const apiFiles = selectedFiles.map(file => ({
        id: file.id,
        name: file.name,
        size: file.size,
        driveId: file.driveId,
        path: file.path,
        web_url: file.web_url,
      }));

      // Get real MSAL access token
      const accessToken = await Providers.globalProvider.getAccessToken({
        scopes: ['Files.Read.All', 'Sites.Read.All']
      });

      if (!accessToken) {
        setError('Impossible d\'obtenir le token d\'accès SharePoint');
        return;
      }

      await uploadSharePointFiles(chatbotId, apiFiles, accessToken);
      onUploadComplete();
      setSelectedFiles([]);
    } catch (error) {
      console.error('Upload failed:', error);
      setError('Échec de l\'upload des fichiers SharePoint');
    }
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '16px',
        flexDirection: 'column',
        gap: '8px'
      }}>
        <Spinner size="small" />
        <Text size={200}>Vérification...</Text>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div style={{ padding: '12px', textAlign: 'center', border: '1px solid #e1e1e1', borderRadius: '6px' }}>
        <Text size={200} style={{ color: '#666', marginBottom: '8px', display: 'block' }}>
          Connexion SharePoint requise
        </Text>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: '8px' }}>
            {error}
          </MessageBar>
        )}

        <Button
          appearance="primary"
          size="small"
          icon={<CloudArrowUp24Regular />}
          onClick={handleSignIn}
        >
          Se connecter
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Connected Status */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        padding: '6px 8px',
        background: '#f0f7ff',
        border: '1px solid #cce7ff',
        borderRadius: '4px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <CheckmarkCircle24Regular style={{ color: '#107c10', fontSize: '16px' }} />
          <Text size={200} weight="semibold">Connecté</Text>
        </div>
        <Button
          appearance="subtle"
          size="small"
          icon={<SignOut24Regular />}
          onClick={handleSignOut}
          style={{ minWidth: 'auto', padding: '2px' }}
        />
      </div>

      {/* Browse Button */}
      <Button
        appearance="primary"
        size="small"
        icon={<CloudArrowUp24Regular />}
        onClick={handleBrowseSharePoint}
        style={{ width: '100%', marginBottom: '8px' }}
      >
        Parcourir SharePoint
      </Button>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #e1e1e1',
          borderRadius: '4px',
          padding: '8px',
          marginBottom: '8px',
        }}>
          <Text weight="semibold" size={200} style={{ display: 'block', marginBottom: '4px' }}>
            Sélectionnés ({selectedFiles.length})
          </Text>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {selectedFiles.map((file, index) => (
              <Text key={index} size={100} style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {file.name}
              </Text>
            ))}
          </div>

          <Button
            appearance="primary"
            size="small"
            icon={<CloudArrowUp24Regular />}
            onClick={handleUpload}
            disabled={isUploading}
            style={{ width: '100%', marginTop: '8px' }}
          >
            {isUploading ? 'Upload...' : `Importer (${selectedFiles.length})`}
          </Button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <MessageBar intent="error" style={{ marginTop: '8px' }}>
          <ErrorCircle24Regular />
          {error}
        </MessageBar>
      )}

      {/* Upload Status */}
      {isUploading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px',
          background: '#fff9e6',
          border: '1px solid #ffec99',
          borderRadius: '4px',
          marginTop: '8px',
        }}>
          <Spinner size="tiny" />
          <Text size={200}>Upload en cours...</Text>
        </div>
      )}

      {/* SharePoint Browser Modal */}
      <SharePointBrowserModal
        isOpen={isBrowserModalOpen}
        onClose={() => setIsBrowserModalOpen(false)}
        onFilesSelected={handleFilesSelected}
        multiSelect={true}
      />
    </div>
  );
};

export default SharePointPicker;
